import { useEffect, useState } from "react";

/**
 * Trial-days-remaining widget. Renders only while a vendor is in the
 * 30-day free trial window. Ticks live (updates every 60s) so a user
 * who leaves the tab open sees the counter progress.
 *
 * Props:
 *   trialEndUnix — Unix timestamp (seconds) of trial_end / current_period_end
 *   trialDays    — total trial length in days (default 30, matches Stripe config)
 *   priceLabel   — text shown for the first-charge amount (default "$49.99" first-year promo)
 */
export default function TrialCountdown({ trialEndUnix, trialDays = 30, priceLabel = "$49.99" }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  if (!trialEndUnix) return null;
  const endMs = trialEndUnix * 1000;
  const msPerDay = 86_400_000;
  const remaining = Math.max(0, Math.ceil((endMs - now) / msPerDay));
  const day = Math.min(trialDays, Math.max(1, trialDays - remaining + 1));
  const pct = Math.min(100, Math.max(0, (day / trialDays) * 100));
  const endDateLabel = new Date(endMs).toLocaleDateString(undefined, {
    month: "short", day: "numeric", year: "numeric",
  });

  // Color escalates as we approach charge day
  let barColor = "bg-emerald-500";
  if (remaining <= 3) barColor = "bg-red-500";
  else if (remaining <= 7) barColor = "bg-[#FFD700]";
  else if (remaining <= 14) barColor = "bg-[#FF5F1F]";

  return (
    <div
      data-testid="trial-countdown"
      className="mt-3 border border-white/10 bg-[#0A0A0A] p-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-400">
          Trial · Day{" "}
          <span data-testid="trial-day" className="font-bold text-white tabular-nums">{day}</span>
          <span className="text-zinc-600"> / {trialDays}</span>
        </div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 tabular-nums">
          <span data-testid="trial-remaining">{remaining}</span> {remaining === 1 ? "day" : "days"} left
        </div>
      </div>
      <div className="mt-2 h-1 w-full bg-white/5 overflow-hidden">
        <div
          data-testid="trial-progress-bar"
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-2 text-[11px] leading-snug text-zinc-500">
        First charge of <span className="text-white font-semibold tabular-nums">{priceLabel}</span> on{" "}
        <span data-testid="trial-charge-date" className="text-white">{endDateLabel}</span>. Cancel any time before then for no charge.
      </div>
    </div>
  );
}
